import { GridColDef, GridInputRowSelectionModel, GridRowSelectionModel, GridRowsProp, GridValidRowModel } from "@mui/x-data-grid"
import { Payment, PaymentDetail } from "../../../../../interfaces/Purchases/Payments/payments"
import { UseFormGetValues } from "react-hook-form"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Spinner } from "../../../../../components/Commons/Spinner/Spinner"
import { Backdrop, Box, CircularProgress } from "@mui/material"
import DataTable from "../../../../../components/Tables/GridTableMaterialUI/DataTable"
import useSnackBar from "../../../../../components/Commons/SnackBar/useSnackBar"
import GenericPromises from "../../../../../api/GenericPromises"

type TableApplyPaymentProps = {
  rows: readonly GridValidRowModel[],
  getValues: UseFormGetValues<Payment>,
  setSelectedRows: Dispatch<SetStateAction<readonly GridValidRowModel[]>>,
  onChangeTotals: (myRows: readonly GridValidRowModel[]) => void,
  preSelectedRows: GridInputRowSelectionModel | undefined,
  setPreSelectedRows: Dispatch<SetStateAction<GridInputRowSelectionModel | undefined>>,
  loadingPost: boolean,
  isEdit: boolean
  hasErrorPost: boolean,
  isLoadingCurrenciesValues: boolean,
  myParentColumns: GridColDef<PaymentDetail>[],
  myParentPreferences: {},
}

export const TableApplyPayment = ({
  rows,
  getValues,
  setSelectedRows,
  onChangeTotals,
  preSelectedRows,
  setPreSelectedRows,
  loadingPost,
  isEdit,
  hasErrorPost,
  isLoadingCurrenciesValues,
  myParentColumns,
  myParentPreferences
}: TableApplyPaymentProps) => {
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { GenericGetResource } = GenericPromises();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [columns, setColumns] = useState<GridColDef<PaymentDetail>[]>(myParentColumns);
  const [paymentsDetailsData, setPaymentsDetailsData] = useState<GridRowsProp>([]);
  const [auxPaymentsDetailsData, setAuxPaymentsDetailsData] = useState<GridRowsProp>([]);
  const [myRowsIdsSelected, setMyRowsIdsSelected] = useState<GridRowSelectionModel>([]);
  const [myPreferences, setMyPreferences] = useState({});

  const onRowSelection = async (rowsIds: GridRowSelectionModel) => {
    let myNewSelectedRows = await paymentsDetailsData.filter(item => rowsIds.includes(item.payment_detail_id));
    setSelectedRows((prev) => myNewSelectedRows);
    setPreSelectedRows(rowsIds);
    setMyRowsIdsSelected(rowsIds);
    onChangeTotals(myNewSelectedRows);
  }

  useEffect(() => {
    if (isLoadingCurrenciesValues) { setOpenBackDrop(true); }
    else { setOpenBackDrop(false); }
  }, [isLoadingCurrenciesValues]);

  useEffect(() => {
    const updateData = async () => {
      if (loadingPost || (!loadingPost && getValues("payment_id"))) {
        setAuxPaymentsDetailsData(paymentsDetailsData);
        setPaymentsDetailsData(paymentsDetailsData.filter((item) => myRowsIdsSelected.includes(item.payment_detail_id)));
        if (!hasErrorPost && getValues("payment_id")) {
          try {
            const responseDetails = await GenericGetResource(`/paymentsdetails/bypaymentid/${getValues("payment_id")}`);
            setPaymentsDetailsData(responseDetails.data.data);
          } catch (error) {
            showSnackBar((error as Error).message, 'error');
          }
        }
      } else if (!loadingPost && hasErrorPost) {
        setPaymentsDetailsData(auxPaymentsDetailsData);
      }
    };

    updateData();
  }, [loadingPost, hasErrorPost]);

  useEffect(() => {
    const loadData = async () => {
      setDataLoaded(false);
      setMyPreferences(myParentPreferences);
      const formattedRows: GridRowSelectionModel = preSelectedRows?.toString().split(',').map(id => Number(id)) as GridRowSelectionModel;
      if (getValues("uuid")) {
        setPaymentsDetailsData(rows.filter((item) => formattedRows.includes(item.payment_detail_id)));
        const responseDetails = await GenericGetResource(`/paymentsdetails/bypaymentid/${getValues("payment_id")}`).catch((error) => showSnackBar(error.message, 'error'));
        setPaymentsDetailsData(responseDetails.data.data);
        setDataLoaded(true);
      } else {
        setPaymentsDetailsData(rows);
        preSelectedRows && setMyRowsIdsSelected(formattedRows);
        setDataLoaded(true);
      }
    };

    loadData();
  }, []);

  return (
    <>
      {!dataLoaded && <Spinner isBox={false} />}
      {dataLoaded && <>
        <Box className='screen-container'>
          <div className="d-flex justify-content-center">
            <DataTable
              columns={columns}
              setColumns={setColumns}
              data={paymentsDetailsData}
              entityId={"payment_detail_id"}
              entity={`PaymentsDetails`}
              preferences={myPreferences}
              namePreferences={"/paymentsdetails"}
              nameOrderColumns={"/paymentsdetails"}
              selectRows={!isEdit}
              onRowSelectionModelChange={(rowsIds) => onRowSelection(rowsIds)}
              isChildren={true}
              preSelectedRows={!isEdit ? preSelectedRows : []}
            />
          </div>
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            open={openBackDrop}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <SnackbarComponent />
        </Box>
      </>
      }
    </>
  )
}
